import { events } from 'services/api';
import { buildAnalyticsEventPayload, type AnalyticsContextUser } from 'utils/analyticsEventContext';

import type { FlowThemeId } from './engine/types';
import type { FlowThemeFallbackReason, FlowThemeModeOverride, FlowThemeRenderMode, RendererClass } from './renderBudget';

/**
 * Analytics for the theme performance safeguards, so the team can see what
 * share of learners get the reduced (still) experience. Both events are
 * fire-and-forget: nothing here may throw or reject into the player.
 *
 * - `flow_theme_render_mode`: once per browser session (tab), when the first
 *   learner backdrop mounts. This is the denominator.
 * - `flow_theme_performance_fallback`: when frames are held still for
 *   performance, once per reason and theme per session.
 *
 * Payloads carry device classes and coarse numbers only: no renderer string,
 * no username (the event row already belongs to the signed-in user).
 */
export const RENDER_MODE_EVENT = 'flow_theme_render_mode';
export const PERFORMANCE_FALLBACK_EVENT = 'flow_theme_performance_fallback';
const SENT_KEY = 'wf.flowTheme.analytics';

let sentCache: Set<string> | null = null;

const sentKeys = (): Set<string> => {
  if (sentCache === null) {
    sentCache = new Set();
    try {
      const raw = window.sessionStorage.getItem(SENT_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) {
        parsed.forEach((key) => typeof key === 'string' && sentCache?.add(key));
      }
    } catch {
      // No storage: once per page load instead.
    }
  }
  return sentCache;
};

/** Marks `key` as sent for this session; false if it already was. */
const claimOnce = (key: string): boolean => {
  const sent = sentKeys();
  if (sent.has(key)) {
    return false;
  }
  sent.add(key);
  try {
    window.sessionStorage.setItem(SENT_KEY, JSON.stringify(Array.from(sent)));
  } catch {
    // Still deduplicated for this page load.
  }
  return true;
};

/** The signed-in user cached by AuthContext, or null when signed out (then nothing is sent). */
const cachedUser = (): AnalyticsContextUser | null => {
  try {
    const raw = window.localStorage.getItem('user');
    const user: unknown = raw ? JSON.parse(raw) : null;
    return user && typeof user === 'object' ? (user as AnalyticsContextUser) : null;
  } catch {
    return null;
  }
};

const round = (value: number | null | undefined, digits = 1) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
};

const send = (eventType: string, data: Record<string, unknown>) => {
  try {
    const user = cachedUser();
    if (!user) {
      return;
    }
    const payload: Record<string, unknown> = buildAnalyticsEventPayload({ ...data, surface: 'question_flow' }, user);
    // The event row is already linked to the user; keep names out of the payload.
    delete payload.user_name;
    Promise.resolve(events.track(eventType, payload)).catch(() => undefined);
  } catch {
    // Analytics must never affect the learner.
  }
};

const deviceFields = () => {
  const nav = typeof navigator !== 'undefined'
    ? (navigator as Navigator & { deviceMemory?: number })
    : undefined;
  return {
    dpr: round(typeof window !== 'undefined' ? window.devicePixelRatio : null, 2),
    hardware_concurrency: typeof nav?.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : null,
    device_memory: typeof nav?.deviceMemory === 'number' ? nav.deviceMemory : null,
  };
};

export interface RenderModeReport {
  theme: FlowThemeId;
  mode: FlowThemeRenderMode;
  rendererClass: RendererClass;
  override: FlowThemeModeOverride | null;
}

/** Logs `flow_theme_render_mode` for the first learner backdrop of the session. */
export const trackFlowThemeRenderMode = ({ theme, mode, rendererClass, override }: RenderModeReport) => {
  if (!claimOnce(RENDER_MODE_EVENT)) {
    return;
  }
  send(RENDER_MODE_EVENT, {
    action: 'render_mode',
    mode,
    theme,
    renderer_class: rendererClass,
    override,
    ...deviceFields(),
  });
};

export interface PerformanceFallbackReport {
  theme: FlowThemeId;
  reason: FlowThemeFallbackReason;
  /** Backing-store pixels per CSS pixel when frames went still. */
  pixelRatio: number | null;
  /** Median and 95th percentile frame cost (ms) of the last window; null when detected up front. */
  p50Ms: number | null;
  p95Ms: number | null;
  /** Seconds the scene animated before the fallback (0 when detected up front). */
  elapsedSeconds: number;
}

/** Logs `flow_theme_performance_fallback`, once per reason and theme per session. */
export const trackFlowThemePerformanceFallback = (report: PerformanceFallbackReport, override: FlowThemeModeOverride | null = null) => {
  if (!claimOnce(`${PERFORMANCE_FALLBACK_EVENT}:${report.reason}:${report.theme}`)) {
    return;
  }
  send(PERFORMANCE_FALLBACK_EVENT, {
    action: 'performance_fallback',
    reason: report.reason,
    theme: report.theme,
    pixel_ratio: round(report.pixelRatio, 2),
    p50_ms: round(report.p50Ms),
    p95_ms: round(report.p95Ms),
    elapsed_seconds: round(report.elapsedSeconds),
    override,
    ...deviceFields(),
  });
};

/** Test helper: forget what was sent this page load. */
export const resetFlowThemeAnalyticsForTests = () => {
  sentCache = null;
};
